// Calendar — month-grid overview of plans, news, and trades.
//
// Top header: month picker (prev / today / next).
// Grid: 7 columns Sun-Sat, padded with blank cells before 1st-of-month
// and after last-of-month. Each populated cell shows 3 slim badges
// (plan kind / news sentiment / trades count).
//
// Click a cell → below-expand detail section with the full plan,
// the day's summarised news list, and the crypto orders executed.
// Detail fetches reuse existing endpoints (plan latest + news for
// scope + orders list) so the summary endpoint stays cheap.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  CalendarDay,
  CalendarDayCryptoOrder,
  CalendarDayDetailPayload,
  CalendarDayNewsRow,
  CalendarDayStockOrder,
  CalendarPayload,
} from '@/api/client'
import type { TradingPlan } from '@/api/types'
import { Badge, Button, Card } from '@/admin-design/_common'
import { formatTime } from '@/lib/utils'
import { krxName, symbolName } from '@/lib/krx-names'

// ---- helpers ----------------------------------------------------------------

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']

function formatMonthKO(month: string): string {
  const [y, m] = month.split('-')
  return `${y}년 ${parseInt(m, 10)}월`
}

function addMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

function currentMonthKST(): string {
  const now = new Date()
  // Shift to KST for the month label so a user in UTC+9+ sees the
  // correct "this month" click from the 오늘 button.
  const kst = new Date(now.getTime() + 9 * 3600_000)
  return `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}`
}

function todayKST(): string {
  const kst = new Date(Date.now() + 9 * 3600_000)
  return `${kst.getUTCFullYear()}-${String(kst.getUTCMonth() + 1).padStart(2, '0')}-${String(kst.getUTCDate()).padStart(2, '0')}`
}

function sentimentTone(score: number | undefined) {
  if (score === undefined) return 'text-muted-foreground'
  if (score >= 0.2) return 'text-emerald-600'
  if (score <= -0.2) return 'text-red-600'
  return 'text-muted-foreground'
}

// regimeLabel translates the LLM's market-regime call into action-oriented
// Korean. The raw "위험 선호 / 위험 회피" wording was ambiguous — operators
// asked "위험한데 들어가라는 거야?" because '위험' was leading. Re-phrasing
// in terms of what to DO (매수 우호 / 방어적) makes the call self-evident.
function regimeLabel(regime: string): string {
  switch (regime) {
    case 'risk_on':
      return '매수 우호'
    case 'risk_off':
      return '방어적'
    case 'degraded':
      return '분석 실패'
    case 'neutral':
      return '중립'
    default:
      return regime
  }
}

// regimeTooltip is the long-form hover for the Calendar cell badge so the
// operator can re-confirm what each regime actually means.
function regimeTooltip(regime: string): string {
  switch (regime) {
    case 'risk_on':
      return '매수 우호 (risk-on) — LLM 이 위험자산 익스포저 늘려도 좋다고 판단'
    case 'risk_off':
      return '방어적 (risk-off) — 익스포저 줄이고 현금 비중 권장'
    case 'degraded':
      return 'LLM 분석 실패 — 야간 분석 재실행 필요'
    case 'neutral':
      return '중립 — 신호 약함, 기존 포지션 유지'
    default:
      return regime
  }
}

// ---- main page --------------------------------------------------------------

export default function Calendar() {
  const [month, setMonth] = useState<string>(currentMonthKST())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  const q = useQuery<CalendarPayload>({
    queryKey: ['calendar', month],
    queryFn: () => api.dashboardCalendar(month),
    refetchOnWindowFocus: false,
  })

  // Build grid cells: pad leading Sun..(weekday-1) with null, then
  // every day of the month, then trailing (6-lastWeekday).
  const cells = useMemo(() => {
    if (!q.data) return []
    const days = q.data.days
    if (days.length === 0) return []
    const first = days[0]
    const leadingPad = first.weekday // Sun=0 → 0 pad, Mon=1 → 1 pad, …
    const last = days[days.length - 1]
    const trailingPad = 6 - last.weekday
    const out: (CalendarDay | null)[] = []
    for (let i = 0; i < leadingPad; i++) out.push(null)
    out.push(...days)
    for (let i = 0; i < trailingPad; i++) out.push(null)
    return out
  }, [q.data])

  const selectedDay = useMemo(() => {
    if (!selectedDate || !q.data) return null
    return q.data.days.find((d) => d.date === selectedDate) ?? null
  }, [selectedDate, q.data])

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <h1 className="text-lg font-semibold whitespace-nowrap">달력</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="whitespace-nowrap"
            onClick={() => setMonth((m) => addMonth(m, -1))}
          >
            ◀ 이전
          </Button>
          <span className="min-w-[6rem] text-center text-sm font-semibold whitespace-nowrap">
            {formatMonthKO(month)}
          </span>
          <Button
            size="sm"
            variant="secondary"
            className="whitespace-nowrap"
            onClick={() => setMonth((m) => addMonth(m, 1))}
          >
            다음 ▶
          </Button>
          <Button
            size="sm"
            variant="primary"
            className="whitespace-nowrap"
            onClick={() => setMonth(currentMonthKST())}
          >
            오늘
          </Button>
        </div>
      </div>

      <Card>
        <Card.Body>
          {q.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div>
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-muted-foreground">
                {WEEKDAYS.map((d, i) => (
                  <div
                    key={d}
                    className={
                      i === 0
                        ? 'text-red-600'
                        : i === 6
                        ? 'text-blue-600'
                        : ''
                    }
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((cell, idx) =>
                  cell ? (
                    <DayCell
                      key={cell.date}
                      day={cell}
                      selected={cell.date === selectedDate}
                      isFuture={cell.date > todayKST()}
                      onClick={() => setSelectedDate(cell.date)}
                    />
                  ) : (
                    <div
                      key={`pad-${idx}`}
                      className="min-h-[96px] rounded-md border border-dashed border-border/40 bg-muted/10"
                    />
                  ),
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {selectedDay && <DayDetail day={selectedDay} />}
    </div>
  )
}

// ---- cell -------------------------------------------------------------------

function DayCell({
  day,
  selected,
  isFuture,
  onClick,
}: {
  day: CalendarDay
  selected: boolean
  isFuture: boolean
  onClick: () => void
}) {
  const dayNum = parseInt(day.date.split('-')[2], 10)
  const isSun = day.weekday === 0
  const isSat = day.weekday === 6
  // Plan tone — final + regime color accent, draft muted. Future cells
  // get a dimmed dot so the operator can tell at a glance "이건 아직
  // 실행 안 된 예정 plan" (e.g., Friday-after-close cron pre-stages
  // Monday's plan, which legitimately lives in the future column).
  const planDot = isFuture
    ? 'bg-muted-foreground/40'
    : day.plan?.kind === 'final'
      ? day.plan.regime === 'risk_on'
        ? 'bg-emerald-500'
        : day.plan.regime === 'risk_off'
        ? 'bg-red-500'
        : day.plan.regime === 'degraded'
        ? 'bg-amber-500'
        : 'bg-slate-500'
      : day.plan?.kind === 'draft'
      ? 'bg-amber-300'
      : null
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex min-h-[96px] flex-col items-start gap-1 rounded-md border p-1.5 text-left text-[11px] transition-colors ' +
        (selected
          ? 'border-primary bg-primary/10'
          : 'border-border bg-card hover:bg-muted/40') +
        (isFuture ? ' opacity-60' : '')
      }
    >
      <div className="flex w-full items-center justify-between">
        <span
          className={
            'font-mono text-xs font-semibold ' +
            (isSun ? 'text-red-600' : isSat ? 'text-blue-600' : '')
          }
        >
          {dayNum}
        </span>
        {planDot && <span className={`h-2 w-2 rounded-full ${planDot}`} />}
      </div>
      {day.plan && (
        <div className="flex flex-wrap items-center gap-1">
          <span
            className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title={
              isFuture
                ? '아직 실행 안 된 미래 plan'
                : day.plan.kind === 'draft'
                ? '휴장일 관찰 드래프트'
                : regimeTooltip(day.plan.regime)
            }
          >
            {isFuture
              ? '예정'
              : day.plan.kind === 'draft'
              ? '드래프트'
              : regimeLabel(day.plan.regime)}
          </span>
          {day.plan.watchlist_count > 0 && (
            <span className="text-[10px] text-muted-foreground">
              종목 {day.plan.watchlist_count}
            </span>
          )}
        </div>
      )}
      {day.news && (
        // flex-wrap so the +0.21 sentiment drops to a new line on a
        // narrow 7-column mobile cell instead of overflowing/clipping.
        <div className={`flex flex-wrap items-baseline gap-x-1 text-[10px] tabular-nums ${sentimentTone(day.news.avg_sentiment)}`}>
          <span>뉴스 {day.news.summarised}</span>
          <span className="font-mono">
            {day.news.avg_sentiment >= 0 ? '+' : ''}
            {day.news.avg_sentiment.toFixed(2)}
          </span>
        </div>
      )}
      {day.trades && (
        <div className="flex flex-wrap gap-1 text-[10px]">
          {day.trades.crypto_buy > 0 && (
            <span
              className="rounded bg-emerald-100 px-1 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              title="암호화폐 매수"
            >
              ₿매수 {day.trades.crypto_buy}
            </span>
          )}
          {day.trades.crypto_sell > 0 && (
            <span
              className="rounded bg-red-100 px-1 text-red-800 dark:bg-red-950 dark:text-red-300"
              title="암호화폐 매도"
            >
              ₿매도 {day.trades.crypto_sell}
            </span>
          )}
          {day.trades.stock_buy > 0 && (
            <span
              className="rounded bg-emerald-100 px-1 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
              title="주식 매수"
            >
              KR매수 {day.trades.stock_buy}
            </span>
          )}
          {day.trades.stock_sell > 0 && (
            <span
              className="rounded bg-red-100 px-1 text-red-800 dark:bg-red-950 dark:text-red-300"
              title="주식 매도"
            >
              KR매도 {day.trades.stock_sell}
            </span>
          )}
        </div>
      )}
      {(() => {
        // Invariant: only show an asset's realized P&L when that asset actually
        // traded that day — never "0 거래 but 손익" (the P&L attributes to the
        // close day, which always carries the counted closing trade).
        const t = day.trades
        const hasKis = t ? t.stock_buy + t.stock_sell > 0 : false
        const hasCoin = t ? t.crypto_buy + t.crypto_sell > 0 : false
        const kisPnl = hasKis ? t!.stock_realized_pnl_krw : 0
        const coinPnl = hasCoin ? Number(t!.crypto_realized_pnl_krw) : 0
        if (kisPnl === 0 && coinPnl === 0) return null
        return (
          <div className="flex flex-col gap-0.5 text-[10px] tabular-nums">
            {kisPnl !== 0 && (
              <span
                className={kisPnl > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
                title="KIS 실현손익"
              >
                KIS {kisPnl >= 0 ? '+' : '−'}{new Intl.NumberFormat('ko-KR').format(Math.round(Math.abs(kisPnl)))}
              </span>
            )}
            {coinPnl !== 0 && (
              <span
                className={coinPnl > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
                title="코인 실현손익"
              >
                코인 {coinPnl >= 0 ? '+' : '−'}{new Intl.NumberFormat('ko-KR').format(Math.round(Math.abs(coinPnl)))}
              </span>
            )}
          </div>
        )
      })()}
    </button>
  )
}

// ---- below-expand detail ----------------------------------------------------

function DayDetail({ day }: { day: CalendarDay }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">
          {day.date} ({WEEKDAYS[day.weekday]})
        </h2>
        {day.plan && (
          <Badge
            variant={
              day.plan.kind === 'draft'
                ? 'warning'
                : day.plan.regime === 'risk_on'
                ? 'success'
                : day.plan.regime === 'risk_off'
                ? 'destructive'
                : day.plan.regime === 'degraded'
                ? 'warning'
                : 'muted'
            }
            shape="pill"
          >
            {day.plan.kind === 'draft' ? '드래프트' : regimeLabel(day.plan.regime)}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <DayPlanCard date={day.date} />
        <DayStatsCard day={day} />
      </div>
      <DayDrillThrough date={day.date} />
    </div>
  )
}

// DayDrillThrough fetches the full per-day news + order rows on cell
// click. Deferred until the user actually opens a day so the month
// summary fetch stays lean. Three card-stacked sections — news,
// crypto orders, KIS stock orders — each empty-stated independently.
function DayDrillThrough({ date }: { date: string }) {
  const q = useQuery<CalendarDayDetailPayload>({
    queryKey: ['calendar', 'day', date],
    queryFn: () => api.dashboardCalendarDay(date),
    refetchOnWindowFocus: false,
  })
  if (q.isPending) {
    return (
      <Card>
        <Card.Body>
          <p className="text-xs text-muted-foreground">Loading day detail…</p>
        </Card.Body>
      </Card>
    )
  }
  if (!q.data) return null
  // Same invariant as the month cell: only surface an asset's realized P&L when
  // that asset has orders that day.
  const kisPnl = q.data.stock_orders.length > 0 ? (q.data.stock_realized_pnl_krw ?? 0) : 0
  const coinPnl = q.data.crypto_orders.length > 0 ? Number(q.data.crypto_realized_pnl_krw ?? 0) : 0
  return (
    <div className="space-y-3">
      {(kisPnl !== 0 || coinPnl !== 0) && (
        <div className="flex flex-wrap gap-2 text-sm">
          {kisPnl !== 0 && (
            <span
              className={`rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums ${
                kisPnl > 0
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                  : 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-300'
              }`}
              title="KIS 당일 실현손익"
            >
              실현손익 · KIS&nbsp;
              {kisPnl >= 0 ? '+' : '−'}
              {new Intl.NumberFormat('ko-KR').format(Math.round(Math.abs(kisPnl)))} KRW
            </span>
          )}
          {coinPnl !== 0 && (
            <span
              className={`rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums ${
                coinPnl > 0
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                  : 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-300'
              }`}
              title="코인 당일 실현손익"
            >
              실현손익 · 코인&nbsp;
              {coinPnl >= 0 ? '+' : '−'}
              {new Intl.NumberFormat('ko-KR').format(Math.round(Math.abs(coinPnl)))} KRW
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <DayNewsCard rows={q.data.news} />
        <DayCryptoOrdersCard rows={q.data.crypto_orders} />
        <DayStockOrdersCard rows={q.data.stock_orders} />
      </div>
    </div>
  )
}

function DayNewsCard({ rows }: { rows: CalendarDayNewsRow[] }) {
  return (
    <Card>
      <Card.Body>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground">
            요약 뉴스 ({rows.length})
          </h3>
        </div>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">요약된 뉴스 없음</p>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {rows.slice(0, 40).map((r) => (
              <li
                key={r.id}
                className="rounded border border-border bg-card px-2 py-1.5"
              >
                <div className="flex items-start gap-1.5">
                  <span
                    className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold ${sentimentBadgeClass(r.sentiment)}`}
                    title={`${r.sentiment} · ${r.sentiment_score.toFixed(2)}`}
                  >
                    {sentimentLabelKR(r.sentiment)}
                  </span>
                  {r.url ? (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 flex-1 text-primary hover:underline"
                    >
                      {r.title || r.id}
                    </a>
                  ) : (
                    <span className="min-w-0 flex-1">{r.title || r.id}</span>
                  )}
                </div>
                {r.summary_ko && (
                  <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                    {r.summary_ko}
                  </p>
                )}
                <div className="mt-1 flex gap-x-3 text-[10px] text-muted-foreground">
                  <span>{r.service}</span>
                  {r.origin && <span>{r.origin}</span>}
                  {r.published_at > 0 && (
                    <span>{formatTime(r.published_at)}</span>
                  )}
                </div>
              </li>
            ))}
            {rows.length > 40 && (
              <li className="text-[11px] text-muted-foreground">
                … 외 {rows.length - 40}건 (상위 40건만 표시)
              </li>
            )}
          </ul>
        )}
      </Card.Body>
    </Card>
  )
}

function DayCryptoOrdersCard({ rows }: { rows: CalendarDayCryptoOrder[] }) {
  return (
    <Card>
      <Card.Body>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          암호화폐 주문 ({rows.length})
        </h3>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">체결 내역 없음</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 text-left">시각</th>
                  <th className="py-1 text-left">심볼</th>
                  <th className="py-1 text-left">방향</th>
                  <th className="py-1 text-right">가격</th>
                  <th className="py-1 text-right">체결</th>
                  <th className="py-1 text-left">상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.uuid || `${o.symbol}-${o.submitted_at}`} className="border-t border-border">
                    <td className="py-1 font-mono text-[10px]">
                      {formatTime(o.submitted_at)}
                    </td>
                    <td className="py-1">
                      <span className="font-mono">{o.symbol}</span>
                      {symbolName(o.symbol) && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {symbolName(o.symbol)}
                        </span>
                      )}
                    </td>
                    <td className="py-1">
                      <span
                        className={
                          o.side === 'bid'
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }
                      >
                        {o.side === 'bid' ? '매수' : '매도'}
                      </span>
                    </td>
                    <td className="py-1 text-right font-mono">
                      {formatDecimal(o.price)}
                    </td>
                    <td className="py-1 text-right font-mono">
                      {formatDecimal(o.executed_volume || o.volume)}
                    </td>
                    <td className="py-1">
                      <span className="rounded bg-muted px-1 py-0.5 text-[10px]">
                        {o.state}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function DayStockOrdersCard({ rows }: { rows: CalendarDayStockOrder[] }) {
  return (
    <Card>
      <Card.Body>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          KIS 주식 주문 ({rows.length})
        </h3>
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">체결 내역 없음</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 text-left">시각</th>
                  <th className="py-1 text-left">티커</th>
                  <th className="py-1 text-left">방향</th>
                  <th className="py-1 text-right">수량</th>
                  <th className="py-1 text-right">가격</th>
                  <th className="py-1 text-left">상태</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <td className="py-1 font-mono text-[10px]">
                      {formatTime(o.submitted_at)}
                    </td>
                    <td className="py-1">
                      <span className="font-mono">{o.ticker}</span>
                      {krxName(o.ticker) && (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          {krxName(o.ticker)}
                        </span>
                      )}
                    </td>
                    <td className="py-1">
                      <span
                        className={
                          o.side === 'buy'
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }
                      >
                        {o.side === 'buy' ? '매수' : '매도'}
                      </span>
                    </td>
                    <td className="py-1 text-right font-mono">
                      {o.qty.toLocaleString()}
                    </td>
                    <td className="py-1 text-right font-mono">
                      {o.order_type === 'market'
                        ? '시장가'
                        : o.price.toLocaleString()}
                    </td>
                    <td className="py-1">
                      <span className="rounded bg-muted px-1 py-0.5 text-[10px]">
                        {o.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function sentimentLabelKR(s: string): string {
  switch (s) {
    case 'bullish':
      return '강세'
    case 'bearish':
      return '약세'
    case 'mixed':
      return '혼조'
    case 'neutral':
      return '중립'
    default:
      return s
  }
}

function sentimentBadgeClass(s: string): string {
  switch (s) {
    case 'bullish':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
    case 'bearish':
      return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
    case 'mixed':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

// formatDecimal renders a decimal-string (or number) with ko-KR
// thousands separators, tolerating empty/NaN/"0" without crashing.
function formatDecimal(s: string | number | undefined): string {
  if (s === undefined || s === null || s === '') return '-'
  const n = typeof s === 'number' ? s : Number(s)
  if (!Number.isFinite(n)) return String(s)
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 8 }).format(n)
}

function DayPlanCard({ date }: { date: string }) {
  // Reuse the /api/admin/plan endpoint for the full plan body. This
  // returns 404 when no row exists — render the empty state directly
  // so we don't block the whole detail panel.
  const q = useQuery<TradingPlan>({
    queryKey: ['plan-by-date', date],
    queryFn: () => api.planForDate(date),
    refetchOnWindowFocus: false,
    retry: false,
  })
  return (
    <Card>
      <Card.Body>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          플랜
        </h3>
        {q.isPending ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : q.error || !q.data ? (
          <p className="text-xs text-muted-foreground">플랜 없음</p>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Regime</span>
              <span className="font-semibold">
                {regimeLabel(q.data.regime)}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                · #{q.data.id} · {q.data.kind ?? 'final'}
              </span>
            </div>
            {q.data.macro_summary && (
              <p className="whitespace-pre-wrap text-xs text-foreground">
                {q.data.macro_summary}
              </p>
            )}
            {q.data.watchlist.length > 0 && (
              <ul className="space-y-1">
                {q.data.watchlist.map((t) => (
                  <li
                    key={t.symbol}
                    className="flex items-baseline gap-2 text-xs"
                  >
                    <span className="font-mono">{t.symbol}</span>
                    {(t.name || krxName(t.symbol)) && (
                      <span className="text-muted-foreground">
                        {t.name || krxName(t.symbol)}
                      </span>
                    )}
                    <span
                      className={
                        'rounded-full border px-1.5 py-0.5 text-[10px] ' +
                        (t.side === 'long'
                          ? 'border-green-300 text-green-700 bg-green-50'
                          : 'border-red-300 text-red-700 bg-red-50')
                      }
                    >
                      {t.side === 'long' ? '매수' : '매도'}
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      c{t.conviction}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {q.data.risk_notes && (
              <p className="text-[11px] text-muted-foreground">
                <b>Risk</b>: {q.data.risk_notes}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground">
              Generated {formatTime(q.data.created_at)}
            </p>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function DayStatsCard({ day }: { day: CalendarDay }) {
  return (
    <Card>
      <Card.Body>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          일별 요약
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-xs text-muted-foreground">요약된 뉴스</dt>
            <dd className="font-mono">
              {day.news ? day.news.summarised : 0}건
              {day.news && (
                <span
                  className={`ml-2 text-[11px] ${sentimentTone(day.news.avg_sentiment)}`}
                >
                  {day.news.avg_sentiment >= 0 ? '+' : ''}
                  {day.news.avg_sentiment.toFixed(2)}
                </span>
              )}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-xs text-muted-foreground">매매 (암호화폐)</dt>
            <dd className="font-mono text-xs">
              {day.trades ? (
                <>
                  <span className="text-emerald-600">
                    매수 {day.trades.crypto_buy}
                  </span>
                  <span className="mx-1">·</span>
                  <span className="text-red-600">
                    매도 {day.trades.crypto_sell}
                  </span>
                </>
              ) : (
                '없음'
              )}
            </dd>
          </div>
          {day.trades && Number(day.trades.crypto_notional_krw) > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-xs text-muted-foreground">체결 금액</dt>
              <dd className="font-mono">
                {new Intl.NumberFormat('ko-KR').format(
                  Number(day.trades.crypto_notional_krw),
                )}{' '}
                KRW
              </dd>
            </div>
          )}
        </dl>
      </Card.Body>
    </Card>
  )
}
